import React, { useState } from "react";
import { FormSection } from "../../../../../components/FormSection/FormSection";
import { Divider } from "../../../../../components/core/Divider/Divider";
import { ReactComponent as DetailsIcon } from "../../../../../icons/details.svg";
import { ReactComponent as BriefcaseIcon } from "../../../../../icons/briefcase.svg";
import {
  PROFESSIONAL_DATA_LABEL,
  YEARS_IN_ABSTRACT,
  YEARS_OF_EXPERIENCE,
  EXPERIENCES_LABEL,
  EXPERIENCE_LABEL,
  EMPTY_STRING,
} from "../../../../../constants/Dictionary";
import { StyledProfessionalPortrait } from "./ProfessionalPortait.styled";
import { Loader } from "../../../../../components/core/Loader/Loader";
import { TextBox } from "../../../../../components/TextBoxGrid/TextBoxGrid.component";
import LinkedinCard from "./LinkedinCard/LinkedinCard";
import PastExperiences from "./PastExperiences/PastExperiences";
import { Link, LinkSizeEnum } from "../../../../../components/core/Link/Link";
import { ReactComponent as AddIcon } from "../../../../../icons/add.svg";
import { useAppSelector } from "../../../../../hooks/store.hook";
import { getYearsAndMonths } from "../../../../../utils/date/date.utils";
import {
  CollaboratorCmSheet,
  CollaboratorDetails,
} from "../../../../../types/api/collaborators.type";
import { canEditCollaboratorHistory } from "../../../../../utils/canEditCollaboratorHistory.utils";
import { useProfessionalPortrait } from "../../../../../hooks/professionalPortrait.hook";

type ProfessionalPortraitProps = {
  collaborator: CollaboratorDetails;
  onStatusRefresh: () => void;
  isMyProfile?: boolean;
  setHistoryRefetchFn?: (fn: () => void) => void;
  currentCmSheet?: CollaboratorCmSheet;
};

export const ProfessionalPortrait = ({
  collaborator,
  onStatusRefresh,
  isMyProfile,
  currentCmSheet,
}: ProfessionalPortraitProps) => {
  const { profile } = useAppSelector((state) => state.user);
  const profileId = collaborator.id;

  const [showAddModal, setShowAddModal] = useState(false);
  const [hasExperiencesData, setHasExperiencesData] = useState(false);

  const { data: professionalData, isFetching: professionalDataLoading } =
    useProfessionalPortrait(profileId);

  const timeOfExperienceTotal =
    professionalData?.monthsOfExperienceTotal !== undefined &&
    professionalData?.monthsOfExperienceTotal !== null
      ? getYearsAndMonths(professionalData?.monthsOfExperienceTotal)
      : undefined;
  const timeOfExperienceAbstract =
    professionalData?.monthsOfExperienceAbstract !== undefined &&
    professionalData?.monthsOfExperienceAbstract !== null
      ? getYearsAndMonths(professionalData?.monthsOfExperienceAbstract)
      : undefined;
  const linkedInUrl =
    professionalData?.urls && professionalData?.urls.length > 0
      ? professionalData?.urls[0].linkedInUrl
      : undefined;
  const professionalDataId =
    professionalData?.urls && professionalData?.urls.length > 0
      ? professionalData?.urls[0].id
      : undefined;

  const canEditHistory = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
  );
  const restrictCmActions = currentCmSheet?.careerManagerId === profile.id;

  const canEditExperiences = canEditCollaboratorHistory(
    profile,
    collaborator,
    currentCmSheet,
    isMyProfile,
    restrictCmActions,
  );

  return (
    <StyledProfessionalPortrait>
      <FormSection label={PROFESSIONAL_DATA_LABEL} icon={<DetailsIcon />}>
        <Loader loading={professionalDataLoading}>
          <div className="content-grid">
            <div className="linkedin-card-container">
              <LinkedinCard
                currentCmSheet={currentCmSheet}
                profileUrl={linkedInUrl}
                professionalDataId={professionalDataId}
                isMyProfile={isMyProfile}
                isEditable={canEditHistory}
                collaborator={collaborator}
              />
            </div>
            <TextBox
              label={YEARS_OF_EXPERIENCE}
              text={
                timeOfExperienceTotal ? timeOfExperienceTotal : EMPTY_STRING
              }
              error={timeOfExperienceTotal === null}
            />
            <TextBox
              label={YEARS_IN_ABSTRACT}
              text={
                timeOfExperienceAbstract
                  ? timeOfExperienceAbstract
                  : EMPTY_STRING
              }
              error={timeOfExperienceAbstract === null}
            />
          </div>
        </Loader>
      </FormSection>
      <Divider />
      <FormSection
        label={EXPERIENCES_LABEL}
        icon={<BriefcaseIcon />}
        rightContent={
          hasExperiencesData && canEditExperiences ? (
            <Link
              onClick={() => {
                setShowAddModal(true);
              }}
              icon={<AddIcon />}
              size={LinkSizeEnum.SMALL}
            >
              {EXPERIENCE_LABEL}
            </Link>
          ) : null
        }
      >
        <PastExperiences
          isMyProfile={isMyProfile}
          showAddModal={showAddModal}
          setShowAddModal={() => setShowAddModal(!showAddModal)}
          onDataChange={setHasExperiencesData}
          onStatusRefresh={onStatusRefresh}
          currentCmSheet={currentCmSheet}
          collaborator={collaborator}
        />
      </FormSection>
    </StyledProfessionalPortrait>
  );
};
